import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type FocusEvent as ReactFocusEvent,
  type RefObject,
} from "react";
import { Link, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { DEFAULT_LOCALE, Locale, useLocale } from "@/hooks/use-locale";
import { cn } from "@/lib/utils";
import { getPreloadedMenuNodes, localeKeyForMenu, setPreloadedMenuNodes } from "@/lib/menu-preload";
import { fetchHomePageContent, getCachedHomePageContent } from "@/lib/homepage";
import { getMenu, toSpaHref, type MenuNode } from "@/lib/wp";

const PRIMARY_ORDER = ["solutions", "products", "services", "partners", "company"] as const;
type PrimaryKey = (typeof PRIMARY_ORDER)[number];
const CTA_CLASSNAMES = ["onp-header-cta", "onp-primary-cta", "onp-cta"]; // allow authors to target CTA via menu classes
const PRIMARY_LABELS: Record<PrimaryKey, string> = {
  solutions: "Solutions",
  products: "Products",
  services: "Services",
  partners: "Partners",
  company: "Company",
};

type MegaCard = {
  id: number;
  title: string;
  href: string;
  description?: string;
  icon?: string;
  iconColor?: string;
  mobileOnly?: boolean;
};

type MegaGroup = {
  id: number;
  title: string;
  description?: string;
  icon?: string;
  iconColor?: string;
  items: MegaCard[];
  mobileOnly?: boolean;
};

type PrimaryMenu = {
  key: PrimaryKey;
  title: string;
  href: string;
  icon?: string;
  iconColor?: string;
  groups: MegaGroup[];
  cards: MegaCard[];
  mobileOnly?: boolean;
};

type MenuContent = Record<PrimaryKey, PrimaryMenu>;

type MenuEntry = {
  id: number;
  title: string;
  href: string;
  description?: string;
  icon?: string;
  iconColor?: string;
  mobileOnly?: boolean;
  classes: string[];
  children: MenuEntry[];
};

type HeaderCta = {
  id: number;
  title: string;
  href: string;
};

type LanguageOption = {
  locale: Locale;
  label: string;
  labelShort: string;
  flag: string;
  href: string;
};

type LanguageSwitcherData = {
  current: LanguageOption;
  options: LanguageOption[];
};

const INDICATOR_WIDTH = 25;
const INDICATOR_HALF = INDICATOR_WIDTH / 2;

const SOLUTION_COLORS = ["#2AAFB0", "#9747FF", "#EF974C"];
const PRODUCTS_COLOR = "#D9C478";
const SERVICES_COLOR = "#FFFFFF";
const PARTNERS_COLOR = "#FFFFFF";
const COMPANY_COLOR = "#FFFFFF";
const CARD_MIN_HEIGHT_CLASS = "min-h-[96px]";
const CARD_MIN_HEIGHT_PX = 96;
const SOLUTIONS_ITEMS_PER_COLUMN = 5;
const USE_CASE_ITEMS_PER_COLUMN = 6;
const HOVER_TEXT_LIGHT = "#FFFFFF";
const HOVER_TEXT_DARK = "#303343";
const EMPTY_PRIMARY_MENU = (key: PrimaryKey): PrimaryMenu => ({
  key,
  title: PRIMARY_LABELS[key],
  href: '#',
  groups: [],
  cards: [],
  mobileOnly: false,
});

function hexToRgba(hex: string | undefined, alpha: number, fallback = "#FFFFFF") {
  const source = hex?.trim() || fallback;
  const normalized = source.startsWith("#") ? source.slice(1) : source;
  const value = normalized.length === 3
    ? normalized.split("").map((c) => c + c).join("")
    : normalized;
  const parsed = Number.parseInt(value, 16);
  if (Number.isNaN(parsed)) {
    return `rgba(255,255,255,${alpha})`;
  }
  const r = (parsed >> 16) & 255;
  const g = (parsed >> 8) & 255;
  const b = parsed & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function stripDiacritics(value: string): string {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "");
}

function matchPrimaryKey(entry: MenuEntry): PrimaryKey | null {
  const classMatch = entry.classes
    .map((cls) => cls.toLowerCase())
    .find((cls) => cls.startsWith("onp-primary-"));
  if (classMatch) {
    const suffix = classMatch.replace("onp-primary-", "") as PrimaryKey;
    if (PRIMARY_ORDER.includes(suffix)) {
      return suffix;
    }
  }

  const normalized = stripDiacritics(entry.title.trim().toLowerCase());
  if (normalized.includes("solution")) return "solutions";
  if (normalized.includes("produit") || normalized.includes("product")) return "products";
  if (normalized.includes("service")) return "services";
  if (normalized.includes("partner") || normalized.includes("partenaire")) return "partners";
  if (normalized.includes("company") || normalized.includes("entrepris") || normalized.includes("societe")) return "company";
  return null;
}


function normalizeMenuNodes(nodes: MenuNode[], locale: Locale): MenuEntry[] {
  return nodes.map((node) => ({
    id: node.id,
    title: node.title,
    href: node.url ? toSpaHref(node.url, locale) : "#",
    description: node.description ?? undefined,
    icon: node.icon ?? undefined,
    iconColor: node.iconColor ?? undefined,
    mobileOnly: Boolean(node.mobileOnly),
    classes: (node.classes ?? []).map((cls) => cls.toString()),
    children: normalizeMenuNodes(node.children ?? [], locale),
  }));
}

function entryToCard(entry: MenuEntry, fallbackColor?: string): MegaCard {
  return {
    id: entry.id,
    title: entry.title,
    href: entry.href,
    description: entry.description,
    icon: entry.icon ?? undefined,
    iconColor: entry.iconColor ?? fallbackColor,
    mobileOnly: entry.mobileOnly,
  };
}

function buildPrimaryMenu(key: PrimaryKey, entry: MenuEntry | undefined): PrimaryMenu {
  if (!entry) {
    return EMPTY_PRIMARY_MENU(key);
  }

  const groups: MegaGroup[] = [];
  const cards: MegaCard[] = [];

  (entry.children ?? []).forEach((child, idx) => {
    const defaultColor =
      key === "solutions"
        ? SOLUTION_COLORS[idx % SOLUTION_COLORS.length]
        : key === "products"
        ? PRODUCTS_COLOR
        : key === "services"
        ? SERVICES_COLOR
        : key === "partners"
        ? PARTNERS_COLOR
        : COMPANY_COLOR;

    if (child.children.length > 0) {
      const groupColor = child.iconColor || entry.iconColor || defaultColor;
      const items = child.children.map((grand) => entryToCard(grand, grand.iconColor || groupColor));
      groups.push({
        id: child.id,
        title: child.title,
        description: child.description ?? undefined,
        icon: child.icon ?? undefined,
        iconColor: groupColor,
        items,
        mobileOnly: child.mobileOnly,
      });
    } else {
      cards.push(entryToCard(child, child.iconColor || entry.iconColor || defaultColor));
    }
  });

  return {
    key,
    title: entry.title || PRIMARY_LABELS[key],
    href: entry.href || '#',
    icon: entry.icon ?? undefined,
    iconColor: entry.iconColor ?? undefined,
    groups,
    cards,
    mobileOnly: entry.mobileOnly,
  };
}

function buildMenuContent(entries: MenuEntry[]): MenuContent {
  const primaryAssignments = new Map<PrimaryKey, MenuEntry>();
  const usedEntries = new Set<MenuEntry>();

  entries.forEach((entry) => {
    const key = matchPrimaryKey(entry);
    if (key && !primaryAssignments.has(key)) {
      primaryAssignments.set(key, entry);
      usedEntries.add(entry);
    }
  });

  const remainingKeys = PRIMARY_ORDER.filter((key) => !primaryAssignments.has(key));

  entries.forEach((entry) => {
    if (usedEntries.has(entry)) return;
    const nextKey = remainingKeys.shift();
    if (!nextKey) return;
    primaryAssignments.set(nextKey, entry);
    usedEntries.add(entry);
  });

  const result: Partial<MenuContent> = {};
  PRIMARY_ORDER.forEach((key) => {
    const typed = key as PrimaryKey;
    const entry = primaryAssignments.get(typed);
    result[typed] = buildPrimaryMenu(typed, entry);
  });

  return result as MenuContent;
}

const EMPTY_MENU: MenuContent = {
  solutions: EMPTY_PRIMARY_MENU("solutions"),
  products: EMPTY_PRIMARY_MENU("products"),
  services: EMPTY_PRIMARY_MENU("services"),
  partners: EMPTY_PRIMARY_MENU("partners"),
  company: EMPTY_PRIMARY_MENU("company"),
};

function useMenuContent(locale: Locale): { content: MenuContent; cta: HeaderCta | null; ready: boolean } {
  const localeKey = localeKeyForMenu(locale);
  const preloadedNodes = useMemo(() => getPreloadedMenuNodes(localeKey, "header"), [localeKey]);
  const [entries, setEntries] = useState<MenuEntry[] | null>(() =>
    preloadedNodes ? normalizeMenuNodes(preloadedNodes, locale) : null
  );
  const [ready, setReady] = useState(() => preloadedNodes != null);

  useEffect(() => {
    if (preloadedNodes) {
      setEntries(normalizeMenuNodes(preloadedNodes, locale));
      setReady(true);
      return;
    }
    setEntries(null);
    setReady(false);
  }, [preloadedNodes, locale]);

  useEffect(() => {
    if (preloadedNodes) {
      return;
    }
    let active = true;
    getMenu("header", locale === DEFAULT_LOCALE ? undefined : locale)
      .then((items) => {
        if (!active) return;
        setPreloadedMenuNodes(localeKey, "header", items);
        setEntries(normalizeMenuNodes(items, locale));
        setReady(true);
      })
      .catch(() => {
        if (active) {
          setEntries(null);
          setReady(true);
        }
      });

    return () => {
      active = false;
    };
  }, [locale, localeKey, preloadedNodes]);

  return useMemo(() => {
    const normalized = entries ?? [];
    const content = ready ? buildMenuContent(normalized) : EMPTY_MENU;
    const ctaEntry = normalized.find((entry) => {
      const classList = entry.classes.map((cls) => cls.toLowerCase());
      return CTA_CLASSNAMES.some((needle) => classList.includes(needle));
    });

    const cta = ctaEntry
      ? {
          id: ctaEntry.id,
          title: ctaEntry.title,
          href: ctaEntry.href,
        }
      : null;

    return { content, cta, ready };
  }, [entries, ready]);
}

function useLanguageSwitcherData(locale: Locale): LanguageSwitcherData {
  const location = useLocation();

  return useMemo(() => {
    const rawPath = location.pathname || "/";
    const normalized = rawPath.replace(/\/+/g, "/");
    const canonical = normalized !== "/" ? normalized.replace(/\/$/, "") : normalized;

    const sanitizeEnglish = () => {
      if (canonical.startsWith("/fr")) {
        const stripped = canonical.replace(/^\/fr/, "");
        return stripped === "" ? "/" : stripped;
      }
      return canonical === "" ? "/" : canonical;
    };

    let englishPath = sanitizeEnglish();
    if (!englishPath.startsWith("/")) {
      englishPath = `/${englishPath}`;
    }
    if (englishPath === "") {
      englishPath = "/";
    }

    const frenchPath = canonical.startsWith("/fr")
      ? (canonical === "" ? "/fr" : canonical)
      : canonical === "/"
      ? "/fr"
      : `/fr${canonical}`;

    const options: LanguageOption[] = [
      { locale: "en", label: "English", labelShort: "EN", flag: "https://ct-prod.s3-eu-west-1.amazonaws.com/assets/temp-onplexus/core/Flag-GBP.svg", href: englishPath },
      { locale: "fr", label: "Français", labelShort: "FR", flag: "https://ct-prod.s3-eu-west-1.amazonaws.com/assets/temp-onplexus/core/Flag-FAR.svg", href: frenchPath },
    ];

    const current = options.find((option) => option.locale === locale) ?? options[0];

    return { current, options };
  }, [locale, location.pathname]);
}

function splitTwoColumns(items: MegaCard[]) {
  const filtered = items.filter((card) => !card.mobileOnly);
  const rows = Math.max(1, Math.ceil(filtered.length / 2));
  const first = filtered.slice(0, rows);
  const second = filtered.slice(rows, rows * 2);
  return { rows, columns: [first, second] as [MegaCard[], MegaCard[]] };
}

function buildCardStyle(color: string, fallback: string, hoverText: string): CSSProperties {
  const resolvedColor = color || fallback;
  const style: CSSProperties = {};
  (style as Record<string, string>)["--mega-icon-color"] = resolvedColor;
  (style as Record<string, string>)["--mega-icon-bg"] = hexToRgba(resolvedColor, 0.15, fallback);
  (style as Record<string, string>)["--mega-icon-hover-text"] = hoverText;
  return style;
}

function measureMenuAlignment(button: HTMLElement, container: HTMLElement, margin = 16, bounds?: DOMRect) {
  const previousTransform = container.style.transform;
  container.style.transform = 'translateX(0px)';

  const buttonRect = button.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const viewportWidth = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
  const leftBoundary = bounds ? bounds.left + margin : margin;
  const rightBoundary = bounds ? bounds.right - margin : viewportWidth - margin;

  const navCenter = buttonRect.left + buttonRect.width / 2;
  const containerWidth = containerRect.width;

  const desiredLeft = Math.min(
    Math.max(navCenter - containerWidth / 2, leftBoundary),
    Math.max(leftBoundary, rightBoundary - containerWidth)
  );

  const offset = desiredLeft - containerRect.left;

  const indicatorMin = 8;
  const indicatorMax = Math.max(indicatorMin, containerWidth - INDICATOR_WIDTH - indicatorMin);
  const indicatorRaw = navCenter - desiredLeft - INDICATOR_HALF;
  const indicatorLeft = Math.min(Math.max(indicatorRaw, indicatorMin), indicatorMax);

  container.style.transform = previousTransform;

  return { offset, indicatorLeft };
}

type AlignmentEffectOptions = {
  isOpen: boolean;
  menuReady: boolean;
  buttonRef: RefObject<HTMLElement>;
  containerRef: RefObject<HTMLElement>;
  boundsRef?: RefObject<HTMLElement>;
  onOffsetChange: (value: number) => void;
  onIndicatorChange: (value: number) => void;
};

function useMenuAlignmentEffect({
  isOpen,
  menuReady,
  buttonRef,
  containerRef,
  boundsRef,
  onOffsetChange,
  onIndicatorChange,
}: AlignmentEffectOptions) {
  useEffect(() => {
    if (!isOpen || !menuReady) {
      return;
    }

    let rafId: number | null = null;

    const runMeasure = () => {
      if (!buttonRef.current || !containerRef.current) {
        return;
      }
      const bounds = boundsRef?.current?.getBoundingClientRect();
      const { offset, indicatorLeft } = measureMenuAlignment(
        buttonRef.current,
        containerRef.current,
        16,
        bounds ?? undefined,
      );
      onOffsetChange(offset);
      onIndicatorChange(indicatorLeft);
    };

    const scheduleMeasure = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        rafId = null;
        runMeasure();
      });
    };

    scheduleMeasure();
    window.addEventListener("resize", scheduleMeasure);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [isOpen, menuReady, buttonRef, containerRef, boundsRef, onOffsetChange, onIndicatorChange]);
}

type DesktopLanguageSwitcherProps = {
  data: LanguageSwitcherData;
  isOpen: boolean;
  onOpen: () => void;
  onRequestClose: () => void;
  onCloseImmediate: () => void;
  containerRef: React.RefObject<HTMLDivElement>;
};

const DesktopLanguageSwitcher = ({ data, isOpen, onOpen, onRequestClose, onCloseImmediate, containerRef }: DesktopLanguageSwitcherProps) => {
  const { current, options } = data;

  const handleMouseLeave = (event: ReactMouseEvent<HTMLDivElement>) => {
    const nextTarget = (event.relatedTarget as Node | null) ?? null;
    if (containerRef.current && nextTarget && containerRef.current.contains(nextTarget)) {
      return;
    }
    onRequestClose();
  };

  const handleBlur = (event: ReactFocusEvent<HTMLDivElement>) => {
    const nextTarget = (event.relatedTarget as Node | null) ?? null;
    if (containerRef.current && nextTarget && containerRef.current.contains(nextTarget)) {
      return;
    }
    onRequestClose();
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={handleMouseLeave}
      onFocusCapture={onOpen}
      onBlur={handleBlur}
    >
      <button
        type="button"
        className="flex items-center gap-2 lg:gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-white/80 transition-colors hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={current.label}
        onClick={(event) => {
          event.preventDefault();
          if (isOpen) {
            onCloseImmediate();
            onOpen();
          }
        }}
      >
        <img src={current.flag} alt="" className="w-[30px] h-[20px]" aria-hidden />
        <span className="hidden lg:inline">{current.labelShort}</span>
      </button>
      <div
        className={cn(
          "absolute right-0 mt-3 rounded-xl border border-white/10 bg-[#2d3041]/95 p-2 shadow-2xl backdrop-blur",
          "w-max min-w-[72px] lg:min-w-[176px]",
          isOpen ? "block" : "hidden"
        )}
        onMouseEnter={onOpen}
        onMouseLeave={onRequestClose}
        onFocusCapture={onOpen}
        onBlur={onRequestClose}
      >
        {options.map((option) => {
          const isActive = option.locale === current.locale;
          const shouldReload = import.meta.env.PROD && option.locale !== current.locale;
          return (
            <Link
              key={option.locale}
              to={option.href}
              reloadDocument={shouldReload}
              aria-label={option.label}
              className={cn(
                "flex items-center gap-2 lg:gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                isActive ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
              )}
              aria-current={isActive ? "true" : undefined}
              onClick={() => {
                onCloseImmediate();
              }}
            >
              <img src={option.flag} alt="" className="w-[30px] h-[20px]" aria-hidden />
              <span className="hidden flex-1 lg:inline">{option.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

type MobileLanguageSwitcherProps = {
  data: LanguageSwitcherData;
  onSelect?: () => void;
};

const MobileLanguageSwitcher = ({ data, onSelect }: MobileLanguageSwitcherProps) => {
  const { current, options } = data;

  return (
    <div className="px-4 pb-3">
      <p className="text-xs uppercase tracking-[0.2em] text-white/50">
        {current.locale === "fr" ? "Langue" : "Language"}
      </p>
      <div className="mt-2 flex gap-2">
        {options.map((option) => {
          const isActive = option.locale === current.locale;
          const shouldReload = import.meta.env.PROD && option.locale !== current.locale;
          return (
            <Link
              key={option.locale}
              to={option.href}
              onClick={() => {
                onSelect?.();
              }}
              reloadDocument={shouldReload}
              className={cn(
                "flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-sm transition-colors",
                isActive ? "bg-white text-onplexus-dark" : "text-white/80 hover:bg-white/10"
              )}
              aria-current={isActive ? "true" : undefined}
            >
              <img src={option.flag} alt="" className="w-[30px] h-[20px]" aria-hidden />
              <span>{option.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const OnPlexusLogo = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 380 72" className="h-8 w-auto">
    <defs>
      <linearGradient id="header-gradient-1" x1="21.42" y1="-1.07" x2="37.15" y2="33.25" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ebc738" />
        <stop offset=".28" stopColor="#e9c335" />
        <stop offset=".52" stopColor="#e4b92d" />
        <stop offset=".73" stopColor="#dca81f" />
        <stop offset=".93" stopColor="#d1910d" />
      </linearGradient>
      <linearGradient id="header-gradient-2" x1="39.01" y1="66.8" x2="27.09" y2="33.6" gradientUnits="userSpaceOnUse">
        <stop offset="0" stopColor="#ebc738" />
        <stop offset=".27" stopColor="#e9c335" />
        <stop offset=".49" stopColor="#e4b92d" />
        <stop offset=".69" stopColor="#dca81f" />
        <stop offset=".88" stopColor="#d1910d" />
      </linearGradient>
    </defs>
    <path d="M63.09 1.46c0-.38-.3-.68-.68-.68-.12 0-.23.04-.32.09-7.29 4.16-14.74 4.96-22.28 3.68h.03c-2.01-.37-4.09-.58-6.21-.58-8.15 0-15.61 2.96-21.37 7.85a33.24 33.24 0 00-3.58 3.54C3.88 20.9 1 28.1 1 35.98s2.79 14.86 7.43 20.36l11.23-11.23a15.786 15.786 0 01-2.89-9.13c0-8.76 7.1-15.85 15.85-15.85 3.4 0 6.55 1.07 9.13 2.89l11.23-11.23.88-.88 9.1-9.02-.02-.02c.09-.11.15-.25.15-.4z" fill="url(#header-gradient-1)" />
    <path d="M2.14 70.54c0 .38.3.68.68.68.12 0 .23-.04.32-.09 7.29-4.16 14.74-4.96 22.28-3.68h-.03c2.01.37 4.09.58 6.21.58 8.15 0 15.61-2.96 21.37-7.85a33.24 33.24 0 003.58-3.54c4.77-5.54 7.65-12.74 7.65-20.62s-2.79-14.86-7.43-20.36L45.54 26.89c1.82 2.58 2.89 5.73 2.89 9.13 0 8.76-7.1 15.85-15.85 15.85-3.4 0-6.55-1.07-9.13-2.89L12.22 60.21l-.88.88-9.1 9.03.02.02c-.09.11-.15.25-.15.4z" fill="url(#header-gradient-2)" />
    <path fill="#fff" d="M190.97 12.07h-17.35v48.08h8.92V46.78h8.43c3.02 0 5.85-.77 8.33-2.13 5.38-2.95 9.03-8.66 9.03-15.23 0-9.58-7.77-17.35-17.35-17.35zm0 25.8h-8.43V21h8.43c4.66 0 8.44 3.78 8.44 8.44s-3.78 8.44-8.44 8.44zM141.18 39.94v20.78h-8.92V18.51l24.34 22.86v-20.8h8.92v42.2l-24.34-22.83zM298.3 61.04l-8.21-12.81-8.05 12.81h-8.94l12.2-20.68-11.73-20.01h9l7.65 12.07 7.48-12.07h9.08l-11.72 19.9 12.19 20.79h-8.95zM213.89 61.04V20.19h8.74V52.3h15.99v8.74h-24.73zM244.18 61.04V20.35h26.14v8.75h-17.4v7.22h8.7v8.75h-8.7v7.23h17.4v8.74h-26.14zM101.66 10.96c-13.83 0-25.04 11.21-25.04 25.04s11.21 25.04 25.04 25.04S126.7 49.83 126.7 36s-11.21-25.04-25.04-25.04zm0 40.83c-8.72 0-15.79-7.07-15.79-15.79s7.07-15.79 15.79-15.79 15.79 7.07 15.79 15.79-7.07 15.79-15.79 15.79zM335.79 20.35v24.57c0 4.59-3.71 8.31-8.3 8.31s-8.31-3.72-8.31-8.31v-24.6h-8.78v24.6c0 9.44 7.66 17.08 17.08 17.08s17.08-7.71 17.08-17.08V20.35h-8.78zM365.65 37.46c-8.12-2.23-8.8-3.3-8.8-6.02.03-.05-.21-3.62 5.46-3.62 3.5 0 7 1.27 10.39 3.79l4.33-6.27c-4.84-3.5-9.48-5.13-14.6-5.13-13.93 0-13.39 12.25-13.3 12.03.25 6.19 4.08 9.84 12.81 12.2 6.68 1.8 8.24 2.57 8.15 6-.03.05.37 3.91-6.13 3.91-4.22 0-8.13-1.53-11.96-4.68l-4.89 5.86c4.67 4.15 10.59 6.44 16.67 6.44 14.95 0 14.05-12.43 14.03-12.38.13-5.87-3.85-9.83-12.16-12.12" />
  </svg>
);

const BurgerIcon = () => (
  <svg width="34" height="36" viewBox="0 0 34 36" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
    <path d="M8 12H26" stroke="white" strokeWidth="2.07692" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 18H26" stroke="white" strokeWidth="2.07692" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 24H26" stroke="white" strokeWidth="2.07692" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-8 h-8">
    <path d="M8 8L24 24" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 24L24 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackIcon = () => (
  <svg width="61" height="24" viewBox="0 0 61 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex items-center gap-1">
    <path d="M5 12H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 7L5 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 17L5 12" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <text fill="white" xmlSpace="preserve" style={{ whiteSpace: "pre" }} fontFamily="Titillium Web" fontSize="16" letterSpacing="0em">
      <tspan x="28" y="17.96">Back</tspan>
    </text>
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
    <path d="M10 16L14 12L10 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
    <path d="M21 7.5L12 16.5L3 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronUpIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
    <path d="M21 16.5L11.989 7.5L3 16.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Header() {
  const locale = useLocale();
  const { content: menu, cta, ready: menuReady } = useMenuContent(locale);
  const languageData = useLanguageSwitcherData(locale);
  const defaultCtaLabel = locale === "fr" ? "Demander une démo" : "Get a Demo";
  const defaultCtaHref = toSpaHref("/get-a-demo", locale === DEFAULT_LOCALE ? undefined : locale);
  const ctaLabel = (cta?.title?.trim() ?? "") || defaultCtaLabel;
  const ctaHref = cta?.href && cta.href.trim() ? cta.href : defaultCtaHref;
  const resolvedCta: HeaderCta = {
    id: cta?.id ?? -1,
    title: ctaLabel,
    href: ctaHref,
  };
  const isMobile = useIsMobile();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDesktopSolutionsOpen, setIsDesktopSolutionsOpen] = useState(false);
  const [isDesktopProductsOpen, setIsDesktopProductsOpen] = useState(false);
  const [isDesktopServicesOpen, setIsDesktopServicesOpen] = useState(false);
  const [isDesktopPartnersOpen, setIsDesktopPartnersOpen] = useState(false);
  const [isDesktopCompanyOpen, setIsDesktopCompanyOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);

  const solutionsBtnRef = useRef<HTMLAnchorElement>(null);
  const productsBtnRef = useRef<HTMLAnchorElement>(null);
  const servicesBtnRef = useRef<HTMLAnchorElement>(null);
  const partnersBtnRef = useRef<HTMLAnchorElement>(null);
  const companyBtnRef = useRef<HTMLAnchorElement>(null);

  const solutionsMenuContainerRef = useRef<HTMLDivElement>(null);
  const productsMenuContainerRef = useRef<HTMLDivElement>(null);
  const servicesMenuContainerRef = useRef<HTMLDivElement>(null);
  const partnersMenuContainerRef = useRef<HTMLDivElement>(null);
  const companyMenuContainerRef = useRef<HTMLDivElement>(null);

  const solutionsMegaMenuRef = useRef<HTMLDivElement>(null);
  const solutionsBoundsRef = useRef<HTMLDivElement>(null);
  const productsMegaMenuRef = useRef<HTMLDivElement>(null);
  const productsBoundsRef = useRef<HTMLDivElement>(null);
  const servicesMegaMenuRef = useRef<HTMLDivElement>(null);
  const servicesBoundsRef = useRef<HTMLDivElement>(null);
  const partnersMegaMenuRef = useRef<HTMLDivElement>(null);
  const partnersBoundsRef = useRef<HTMLDivElement>(null);
  const companyMegaMenuRef = useRef<HTMLDivElement>(null);
  const companyBoundsRef = useRef<HTMLDivElement>(null);
  const headerSurfaceRef = useRef<HTMLDivElement>(null);
  const languageSurfaceRef = useRef<HTMLDivElement>(null);
  const languageCloseTimerRef = useRef<number | null>(null);
  const pendingSectionRef = useRef<PrimaryKey | null>(null);

  const [solutionsIndicatorLeft, setSolutionsIndicatorLeft] = useState(0);
  const [productsIndicatorLeft, setProductsIndicatorLeft] = useState(0);
  const [servicesIndicatorLeft, setServicesIndicatorLeft] = useState(0);
  const [partnersIndicatorLeft, setPartnersIndicatorLeft] = useState(0);
  const [companyIndicatorLeft, setCompanyIndicatorLeft] = useState(0);
  const [solutionsMenuOffset, setSolutionsMenuOffset] = useState(0);
  const [productsMenuOffset, setProductsMenuOffset] = useState(0);
  const [servicesMenuOffset, setServicesMenuOffset] = useState(0);
  const [partnersMenuOffset, setPartnersMenuOffset] = useState(0);
  const [companyMenuOffset, setCompanyMenuOffset] = useState(0);

  const closeTimerRef = useRef<number | null>(null);

  useEffect(() => {
    // Only fetch menu data for the current locale to avoid language conflicts
    const currentLang = locale === "fr" ? "fr" : undefined;
    if (!getCachedHomePageContent(currentLang)) {
      fetchHomePageContent(currentLang ? { lang: currentLang } : undefined).catch(() => {});
    }
  }, [locale]);

  const cancelClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const closeAll = () => {
    setIsDesktopSolutionsOpen(false);
    setIsDesktopProductsOpen(false);
    setIsDesktopServicesOpen(false);
    setIsDesktopPartnersOpen(false);
    setIsDesktopCompanyOpen(false);
  };

  const isPointerInsideInteractiveArea = (target: Node | null) => {
    if (!target) {
      return false;
    }

    const containers: Array<HTMLElement | null> = [
      headerSurfaceRef.current,
      solutionsMegaMenuRef.current,
      productsMegaMenuRef.current,
      servicesMegaMenuRef.current,
      partnersMegaMenuRef.current,
      companyMegaMenuRef.current,
      languageSurfaceRef.current,
    ];

    return containers.some((element) => element != null && element.contains(target));
  };

  const cancelLanguageClose = () => {
    if (languageCloseTimerRef.current) {
      window.clearTimeout(languageCloseTimerRef.current);
      languageCloseTimerRef.current = null;
    }
  };

  const handleLanguageCloseImmediate = () => {
    cancelLanguageClose();
    setIsLanguageOpen(false);
  };

  const requestLanguageClose = () => {
    cancelLanguageClose();
    languageCloseTimerRef.current = window.setTimeout(() => {
      setIsLanguageOpen(false);
    }, 180);
  };

  const handleLanguageOpen = () => {
    cancelClose();
    cancelLanguageClose();
    closeAll();
    setIsLanguageOpen(true);
  };

  const scheduleClose = (event?: ReactMouseEvent<HTMLElement>) => {
    cancelClose();
    cancelLanguageClose();
    const nextTarget = (event?.relatedTarget as Node | null) ?? null;
    if (isPointerInsideInteractiveArea(nextTarget)) {
      return;
    }

    closeTimerRef.current = window.setTimeout(() => {
      closeAll();
      handleLanguageCloseImmediate();
    }, 200);
  };

  const solutionsGroups = useMemo(() => menu.solutions.groups.filter((group) => group.items.some((card) => !card.mobileOnly)), [menu.solutions]);
  const [activeSolutionsGroupId, setActiveSolutionsGroupId] = useState<number | null>(null);

  useEffect(() => {
    setActiveSolutionsGroupId(solutionsGroups[0]?.id ?? null);
  }, [solutionsGroups]);

  const openSection = (section: PrimaryKey) => {
    setIsDesktopSolutionsOpen(section === "solutions");
    setIsDesktopProductsOpen(section === "products");
    setIsDesktopServicesOpen(section === "services");
    setIsDesktopPartnersOpen(section === "partners");
    setIsDesktopCompanyOpen(section === "company");
  };

  useEffect(() => {
    if (isMobile && isLanguageOpen) {
      handleLanguageCloseImmediate();
    }
  }, [isMobile, isLanguageOpen]);

useEffect(() => () => cancelLanguageClose(), []);

  useMenuAlignmentEffect({
    isOpen: isDesktopSolutionsOpen,
    menuReady,
    buttonRef: solutionsBtnRef,
    containerRef: solutionsMenuContainerRef,
    boundsRef: solutionsBoundsRef,
    onOffsetChange: setSolutionsMenuOffset,
    onIndicatorChange: setSolutionsIndicatorLeft,
  });

  useMenuAlignmentEffect({
    isOpen: isDesktopProductsOpen,
    menuReady,
    buttonRef: productsBtnRef,
    containerRef: productsMenuContainerRef,
    boundsRef: productsBoundsRef,
    onOffsetChange: setProductsMenuOffset,
    onIndicatorChange: setProductsIndicatorLeft,
  });

  useMenuAlignmentEffect({
    isOpen: isDesktopServicesOpen,
    menuReady,
    buttonRef: servicesBtnRef,
    containerRef: servicesMenuContainerRef,
    boundsRef: servicesBoundsRef,
    onOffsetChange: setServicesMenuOffset,
    onIndicatorChange: setServicesIndicatorLeft,
  });

  useMenuAlignmentEffect({
    isOpen: isDesktopPartnersOpen,
    menuReady,
    buttonRef: partnersBtnRef,
    containerRef: partnersMenuContainerRef,
    boundsRef: partnersBoundsRef,
    onOffsetChange: setPartnersMenuOffset,
    onIndicatorChange: setPartnersIndicatorLeft,
  });

  useMenuAlignmentEffect({
    isOpen: isDesktopCompanyOpen,
    menuReady,
    buttonRef: companyBtnRef,
    containerRef: companyMenuContainerRef,
    boundsRef: companyBoundsRef,
    onOffsetChange: setCompanyMenuOffset,
    onIndicatorChange: setCompanyIndicatorLeft,
  });

  useEffect(() => {
    if (!(isDesktopSolutionsOpen || isDesktopProductsOpen || isDesktopServicesOpen || isDesktopPartnersOpen || isDesktopCompanyOpen)) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const inside =
        (solutionsMegaMenuRef.current && solutionsMegaMenuRef.current.contains(target)) ||
        (productsMegaMenuRef.current && productsMegaMenuRef.current.contains(target)) ||
        (servicesMegaMenuRef.current && servicesMegaMenuRef.current.contains(target)) ||
        (partnersMegaMenuRef.current && partnersMegaMenuRef.current.contains(target)) ||
        (companyMegaMenuRef.current && companyMegaMenuRef.current.contains(target));
      if (!inside) {
        setIsDesktopSolutionsOpen(false);
        setIsDesktopProductsOpen(false);
        setIsDesktopServicesOpen(false);
        setIsDesktopPartnersOpen(false);
        setIsDesktopCompanyOpen(false);
        handleLanguageCloseImmediate();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDesktopSolutionsOpen, isDesktopProductsOpen, isDesktopServicesOpen, isDesktopPartnersOpen, isDesktopCompanyOpen]);

  const activeSolutionsGroup = solutionsGroups.find((group) => group.id === activeSolutionsGroupId) ?? solutionsGroups[0];
  const { solutionColumns, solutionRowsPerColumn } = useMemo(() => {
    const filtered = activeSolutionsGroup ? activeSolutionsGroup.items.filter((card) => !card.mobileOnly) : [];
    const normalizedTitle = stripDiacritics(activeSolutionsGroup?.title ?? "").toLowerCase();
    const isUseCase = /use\s*case|cas\s*d'?usage/.test(normalizedTitle);
    const isRole = /role/.test(normalizedTitle);
    const rowsPerColumn = isUseCase
      ? USE_CASE_ITEMS_PER_COLUMN
      : isRole
      ? 2
      : SOLUTIONS_ITEMS_PER_COLUMN;

    if (filtered.length === 0) {
      return {
        solutionColumns: [
          Array.from({ length: rowsPerColumn }).map(() => null),
          Array.from({ length: rowsPerColumn }).map(() => null),
        ],
        solutionRowsPerColumn: rowsPerColumn,
      } as const;
    }

    const baseMinColumns = isUseCase ? 2 : isRole ? 2 : 1;
    const columnCount = Math.max(baseMinColumns, Math.ceil(filtered.length / rowsPerColumn));
    const columns = Array.from({ length: columnCount }).map((_, columnIndex) => {
      const start = columnIndex * rowsPerColumn;
      const slice = filtered.slice(start, start + rowsPerColumn);
      return Array.from({ length: rowsPerColumn }).map((_, idx) => slice[idx] ?? null);
    });

    if (isUseCase && columns.length < 2) {
      columns.push(Array.from({ length: rowsPerColumn }).map(() => null));
    }
    if (isRole && columns.length < 2) {
      columns.push(Array.from({ length: rowsPerColumn }).map(() => null));
    }

    return {
      solutionColumns: columns,
      solutionRowsPerColumn: rowsPerColumn,
    } as const;
  }, [activeSolutionsGroup]);

  const solutionMinHeight = solutionRowsPerColumn * CARD_MIN_HEIGHT_PX;
  const solutionGridColumns = useMemo(
    () => `repeat(${solutionColumns.length}, minmax(0, 1fr))`,
    [solutionColumns.length]
  );

  const productLayout = splitTwoColumns(menu.products.cards);
  const servicesLayout = splitTwoColumns(menu.services.cards);

  const productColumns = productLayout.columns.map((column) =>
    Array.from({ length: productLayout.rows }).map((_, idx) => column[idx] ?? null)
  );
  const servicesColumns = servicesLayout.columns.map((column) =>
    Array.from({ length: servicesLayout.rows }).map((_, idx) => column[idx] ?? null)
  );

  const companyCards = menu.company.cards.filter((card) => !card.mobileOnly);
  const partnerCards = menu.partners.cards.filter((card) => !card.mobileOnly);

  const mobileOnlyLinks = useMemo(() => {
    const links: MegaCard[] = [];
    PRIMARY_ORDER.forEach((key) => {
      const section = menu[key];
      if (section.mobileOnly) {
        links.push({ id: section.key.length * -1000, title: section.title, href: section.href });
      }
      section.cards.forEach((card) => {
        if (card.mobileOnly) {
          links.push(card);
        }
      });
      section.groups.forEach((group) => {
        group.items.forEach((card) => {
          if (card.mobileOnly) {
            links.push(card);
          }
        });
      });
    });
    return links;
  }, [menu]);

  const handleOpen = (section: PrimaryKey) => {
    cancelClose();
    handleLanguageCloseImmediate();
    if (!menuReady) {
      pendingSectionRef.current = section;
      return;
    }
    openSection(section);
  };

  useEffect(() => {
    if (menuReady && pendingSectionRef.current) {
      const section = pendingSectionRef.current;
      pendingSectionRef.current = null;
      if (section) {
        openSection(section);
      }
    }
  }, [menuReady]);

  const topNav = menuReady
    ? PRIMARY_ORDER.map((key) => menu[key]).filter((item) => !item.mobileOnly)
    : [];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 pt-8 px-4">
        <div className="max-w-6xl mx-auto">
          <div
            ref={headerSurfaceRef}
            className="bg-onplexus-darker border border-white/15 rounded-xl px-8 py-4"
            onMouseEnter={cancelClose}
          >
            <div className="flex items-center gap-6">
              <Link to={locale === "fr" ? "/fr" : "/"} aria-label="OnPlexus home" className="shrink-0">
                <OnPlexusLogo />
              </Link>

              {isMobile ? (
                <button
                  onClick={() => {
                    handleLanguageCloseImmediate();
                    setIsMobileMenuOpen(true);
                  }}
                  className="ml-auto p-3 -m-3"
                  aria-label="Open menu"
                >
                  <BurgerIcon />
                </button>
              ) : (
                <div className="ml-auto flex items-center gap-6">
                  <nav className="flex items-center lg:space-x-4" onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
                    {topNav.map((item) => {
                      const refMap = {
                        solutions: solutionsBtnRef,
                        products: productsBtnRef,
                        services: servicesBtnRef,
                        partners: partnersBtnRef,
                        company: companyBtnRef,
                      } as const;
                      const ref = refMap[item.key];
                      return (
                        <div className="relative" key={item.key}>
                          <a
                            ref={ref}
                            href={item.href}
                            className="flex items-center gap-2 text-white font-semibold text-sm hover:text-onplexus-gold transition-colors px-3 py-2 rounded-xl"
                            onMouseEnter={() => handleOpen(item.key)}
                          >
                            {item.title}
                          </a>
                        </div>
                      );
                    })}
                  </nav>
                  <DesktopLanguageSwitcher
                    data={languageData}
                    isOpen={isLanguageOpen}
                    onOpen={handleLanguageOpen}
                    onRequestClose={requestLanguageClose}
                    onCloseImmediate={handleLanguageCloseImmediate}
                    containerRef={languageSurfaceRef}
                  />
                  <Button
                    asChild
                    variant="secondary"
                    className="hidden sm:inline-flex bg-onplexus-gold text-onplexus-dark hover:bg-onplexus-gold/90 font-semibold"
                  >
                    <Link to={resolvedCta.href}>{resolvedCta.title}</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Desktop Solutions Mega Menu */}
      {isDesktopSolutionsOpen && !isMobile && (
        <div
          className="fixed top-[120px] left-0 right-0 z-40"
          ref={solutionsMegaMenuRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div ref={solutionsBoundsRef} className="max-w-6xl mx-auto px-4">
            <div
              className="relative xl:inline-block xl:max-w-[1121px] bg-[#404350] rounded-3xl p-2"
              ref={solutionsMenuContainerRef}
              style={{ transform: `translateX(${solutionsMenuOffset}px)` }}
            >
              <div className="absolute -top-2" style={{ left: `${solutionsIndicatorLeft}px` }}>
                <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0L0.5 8H24.5L12.5 0Z" fill="#404350" />
                </svg>
              </div>

              <div className="hidden md:flex md:flex-col xl:flex-row">
                <div className="xl:w-[360px] flex-shrink-0">
                  <div className="flex xl:flex-col rounded-2xl rounded-r-none p-0">
                    {solutionsGroups.map((group, index) => {
                      const active = group.id === activeSolutionsGroupId;
                      const color = group.iconColor || SOLUTION_COLORS[index % SOLUTION_COLORS.length];
                      const inactiveColor = "rgba(255,255,255,0.12)";
                      return (
                        <button
                          key={group.id}
                          type="button"
                          onMouseEnter={() => setActiveSolutionsGroupId(group.id)}
                          onFocus={() => setActiveSolutionsGroupId(group.id)}
                          onClick={() => setActiveSolutionsGroupId(group.id)}
                          className={cn(
                            "flex flex-col xl:flex-row w-full items-center gap-5 p-6 text-left transition-colors",
                            active
                              ? "gap-4 bg-[#303343] rounded-t-2xl xl:rounded-l-2xl xl:rounded-r-none"
                              : "hover:bg-[#303343]/40"
                          )}
                        >
                          <span
                            className={cn(
                              "block h-1 w-full xl:h-20 xl:w-1 rounded-full transition-opacity",
                              active ? "h-2 w-full xl:h-20 xl:w-2 opacity-100" : "opacity-60"
                            )}
                            style={{ backgroundColor: active ? color : inactiveColor }}
                          />
                          <div className="flex-1">
                            <h3 className="text-white font-bold text-xl leading-6">
                              {group.title.toUpperCase()}
                            </h3>
                            {group.description && (
                              <p className="text-white/70 text-[15px] leading-[21px] mt-2">{group.description}</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-[#303343] rounded-b-2xl xl:rounded-r-2xl xl:rounded-bl-none p-6">
                  <div
                    className="grid gap-0"
                    style={{ gridTemplateColumns: solutionGridColumns }}
                  >
                    {solutionColumns.map((column, columnIndex) => (
                      <div key={`solutions-desktop-col-${columnIndex}`} className="space-y-0">
                        {column.map((card, rowIndex) => {
                          if (!card) {
                            return (
                              <div
                                key={`solutions-desktop-placeholder-${columnIndex}-${rowIndex}`}
                                className={cn("invisible pointer-events-none w-full xl:max-w-[420px]", CARD_MIN_HEIGHT_CLASS)}
                              />
                            );
                          }
                          const baseColor = card.iconColor || activeSolutionsGroup?.iconColor || SOLUTION_COLORS[0];
                          const fallbackColor = activeSolutionsGroup?.iconColor || SOLUTION_COLORS[0];
                          const cardStyle = buildCardStyle(baseColor, fallbackColor, HOVER_TEXT_LIGHT);
                          return (
                            <a
                              key={card.id}
                              href={card.href}
                              className={cn(
                                "group flex items-start gap-4 p-4 rounded-xl w-full xl:max-w-[420px] hover:bg-[#404350] transition-colors",
                                CARD_MIN_HEIGHT_CLASS
                              )}
                              style={cardStyle}
                            >
                              <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors bg-[color:var(--mega-icon-bg)] group-hover:bg-[color:var(--mega-icon-color)]">
                                <span className="mainNav-icon text-xl transition-colors text-[color:var(--mega-icon-color)] group-hover:text-[color:var(--mega-icon-hover-text)]">
                                  {card.icon ?? "•"}
                                </span>
                              </div>
                              <div className="flex-1">
                                <h4 className="text-white font-bold text-xl leading-6">{card.title}</h4>
                                {card.description && (
                                  <p className="text-white/70 text-[15px] leading-[18px] mt-1">{card.description}</p>
                                )}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              </div>


              
            </div>
          </div>
        </div>
      )}

      {/* Desktop Products Mega Menu */}
      {isDesktopProductsOpen && !isMobile && (
        <div
          className="fixed top-[120px] left-0 right-0 z-40"
          ref={productsMegaMenuRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div ref={productsBoundsRef} className="max-w-6xl mx-auto w-max px-4">
            <div
              className="relative xl:inline-block bg-[#404350] rounded-3xl p-2"
              ref={productsMenuContainerRef}
              style={{ transform: `translateX(${productsMenuOffset}px)` }}
            >
              <div className="absolute -top-2" style={{ left: `${productsIndicatorLeft}px` }}>
                <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0L0.5 8H24.5L12.5 0Z" fill="#404350" />
                </svg>
              </div>

              <div className="bg-[#303343] rounded-2xl p-6 w-max">
                <div className="grid grid-cols-2 gap-0 w-max">
                  {productColumns.map((column, columnIndex) => (
                    <div className="space-y-0" key={columnIndex}>
                      {column.map((card, rowIndex) => {
                        if (!card) {
                          return (
                            <div
                              key={`product-placeholder-${columnIndex}-${rowIndex}`}
                              className={cn("invisible pointer-events-none", CARD_MIN_HEIGHT_CLASS)}
                            />
                          );
                        }
                        const baseColor = card.iconColor || PRODUCTS_COLOR;
                        const cardStyle = buildCardStyle(baseColor, PRODUCTS_COLOR, HOVER_TEXT_DARK);
                        return (
                          <a
                            key={card.id}
                            href={card.href}
                            className={cn(
                              "group flex items-start gap-4 p-4 rounded-xl w-full xl:max-w-[420px] hover:bg-[#404350] transition-colors",
                              CARD_MIN_HEIGHT_CLASS
                            )}
                            style={cardStyle}
                          >
                            <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors bg-[color:var(--mega-icon-bg)] group-hover:bg-[color:var(--mega-icon-color)]">
                              <span className="mainNav-icon text-xl transition-colors text-[color:var(--mega-icon-color)] group-hover:text-[color:var(--mega-icon-hover-text)]">
                                {card.icon ?? "•"}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-white font-bold text-xl leading-6">{card.title}</h4>
                              {card.description && (
                                <p className="text-white/70 text-[15px] leading-[18px] mt-1">{card.description}</p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Services Mega Menu */}
      {isDesktopServicesOpen && !isMobile && (
        <div
          className="fixed top-[120px] left-0 right-0 z-40"
          ref={servicesMegaMenuRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div ref={servicesBoundsRef} className="max-w-6xl mx-auto w-max px-4">
            <div
              className="relative xl:inline-block xl:max-w-[1121px] bg-[#404350] rounded-3xl p-2"
              ref={servicesMenuContainerRef}
              style={{ transform: `translateX(${servicesMenuOffset}px)` }}
            >
              <div className="absolute -top-2" style={{ left: `${servicesIndicatorLeft}px` }}>
                <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0L0.5 8H24.5L12.5 0Z" fill="#404350" />
                </svg>
              </div>

              <div className="bg-[#303343] rounded-2xl p-6 xl:w-max">
                <div className="grid grid-cols-2 gap-0 xl:w-max">
                  {servicesColumns.map((column, columnIndex) => (
                    <div className="space-y-0" key={columnIndex}>
                      {column.map((card, rowIndex) => {
                        if (!card) {
                          return (
                            <div
                              key={`services-placeholder-${columnIndex}-${rowIndex}`}
                              className={cn("invisible pointer-events-none", CARD_MIN_HEIGHT_CLASS)}
                            />
                          );
                        }
                        const baseColor = card.iconColor || SERVICES_COLOR;
                        const cardStyle = buildCardStyle(baseColor, SERVICES_COLOR, HOVER_TEXT_DARK);
                        return (
                          <a
                            key={card.id}
                            href={card.href}
                            className={cn(
                              "group flex items-start gap-4 p-4 rounded-xl w-full xl:max-w-[420px] hover:bg-[#404350] transition-colors",
                              CARD_MIN_HEIGHT_CLASS
                            )}
                            style={cardStyle}
                          >
                            <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors bg-[color:var(--mega-icon-bg)] group-hover:bg-[color:var(--mega-icon-color)]">
                              <span className="mainNav-icon text-xl transition-colors text-[color:var(--mega-icon-color)] group-hover:text-[color:var(--mega-icon-hover-text)]">
                                {card.icon ?? "•"}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-white font-bold text-xl leading-6">{card.title}</h4>
                              {card.description && (
                                <p className="text-white/70 text-[15px] leading-[18px] mt-1">{card.description}</p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Partners Mega Menu */}
      {isDesktopPartnersOpen && !isMobile && (
        <div
          className="fixed top-[120px] left-0 right-0 z-40"
          ref={partnersMegaMenuRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div ref={partnersBoundsRef} className="max-w-6xl mx-auto px-4">
            <div
              className="relative md:inline-block md:w-max xl:max-w-[1121px] bg-[#404350] rounded-3xl p-2"
              ref={partnersMenuContainerRef}
              style={{ transform: `translateX(${partnersMenuOffset}px)` }}
            >
              <div className="absolute -top-2" style={{ left: `${partnersIndicatorLeft}px` }}>
                <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0L0.5 8H24.5L12.5 0Z" fill="#404350" />
                </svg>
              </div>

              <div className="bg-[#303343] rounded-2xl p-6 md:w-max xl:w-max">
                <div className="space-y-0 w-full max-w-[360px]">
                  {partnerCards.map((card) => {
                    const baseColor = card.iconColor || PARTNERS_COLOR;
                    const cardStyle = buildCardStyle(baseColor, PARTNERS_COLOR, HOVER_TEXT_DARK);
                    return (
                      <a
                        key={card.id}
                        href={card.href}
                        className="group flex items-start gap-4 p-4 rounded-xl w-full xl:max-w-[360px] hover:bg-[#404350] transition-colors"
                        style={cardStyle}
                      >
                        <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors bg-[color:var(--mega-icon-bg)] group-hover:bg-[color:var(--mega-icon-color)]">
                          <span className="mainNav-icon text-xl transition-colors text-[color:var(--mega-icon-color)] group-hover:text-[color:var(--mega-icon-hover-text)]">
                            {card.icon ?? "•"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-bold text-xl leading-6">{card.title}</h4>
                          {card.description && (
                            <p className="text-white/70 text-[15px] leading-[18px] mt-1">{card.description}</p>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Company Mega Menu */}
      {isDesktopCompanyOpen && !isMobile && (
        <div
          className="fixed top-[120px] left-0 right-0 z-40"
          ref={companyMegaMenuRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div ref={companyBoundsRef} className="max-w-6xl mx-auto px-4">
            <div
              className="relative md:inline-block md:w-max xl:max-w-[1121px] bg-[#404350] rounded-3xl p-2"
              ref={companyMenuContainerRef}
              style={{ transform: `translateX(${companyMenuOffset}px)` }}
            >
              <div className="absolute -top-2" style={{ left: `${companyIndicatorLeft}px` }}>
                <svg width="25" height="8" viewBox="0 0 25 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.5 0L0.5 8H24.5L12.5 0Z" fill="#404350" />
                </svg>
              </div>

              <div className="bg-[#303343] rounded-2xl p-6 md:w-max xl:w-max">
                    <div className="space-y-0 w-full max-w-[360px]">
                  {companyCards.map((card) => {
                    const baseColor = card.iconColor || COMPANY_COLOR;
                    const cardStyle = buildCardStyle(baseColor, COMPANY_COLOR, HOVER_TEXT_DARK);
                    return (
                      <a
                        key={card.id}
                        href={card.href}
                        className="group flex items-start gap-4 p-4 rounded-xl w-full xl:max-w-[360px] hover:bg-[#404350] transition-colors"
                        style={cardStyle}
                      >
                        <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0 transition-colors bg-[color:var(--mega-icon-bg)] group-hover:bg-[color:var(--mega-icon-color)]">
                          <span className="mainNav-icon text-xl transition-colors text-[color:var(--mega-icon-color)] group-hover:text-[color:var(--mega-icon-hover-text)]">
                            {card.icon ?? "•"}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h4 className="text-white font-bold text-xl leading-6">{card.title}</h4>
                          {card.description && (
                            <p className="text-white/70 text-[15px] leading-[18px] mt-1">{card.description}</p>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        menu={menu}
        locale={locale}
        mobileOnlyLinks={mobileOnlyLinks}
        cta={resolvedCta}
        language={languageData}
      />
    </>
  );
}

type MobileMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  menu: MenuContent;
  locale: Locale;
  mobileOnlyLinks: MegaCard[];
  cta: HeaderCta;
  language: LanguageSwitcherData;
};

function MobileMenu({ isOpen, onClose, menu, locale, mobileOnlyLinks, cta, language }: MobileMenuProps) {
  const [view, setView] = useState<"main" | PrimaryKey>("main");
  const solutionsGroups = menu.solutions.groups.filter((group) => group.items.length > 0);
  const [expandedSolutionsGroupId, setExpandedSolutionsGroupId] = useState<number | null>(solutionsGroups[0]?.id ?? null);

  useEffect(() => {
    if (!isOpen) {
      setView("main");
      setExpandedSolutionsGroupId(solutionsGroups[0]?.id ?? null);
    }
  }, [isOpen, solutionsGroups]);

  if (!isOpen) {
    return null;
  }

  const renderMain = () => (
    <div className="flex-1 pt-3">
      <MobileLanguageSwitcher data={language} onSelect={onClose} />
      {PRIMARY_ORDER.map((key) => {
        const section = menu[key];
        if (section.mobileOnly) {
          return null;
        }
        const hasChild = section.groups.length > 0 || section.cards.length > 0;
        return (
          <div key={key} className="flex items-center justify-between w-full px-4 py-3 hover:bg-white/5 transition-colors">
            <a href={section.href || "#"} className="flex-1 text-white font-semibold text-base">
              {section.title}
            </a>
            {hasChild && (
              <button onClick={() => setView(key)} className="flex items-center gap-0 pl-2">
                <span className="text-white text-sm font-normal">{locale === "fr" ? "Voir" : "More"}</span>
                <ChevronRightIcon />
              </button>
            )}
          </div>
        );
      })}
      {mobileOnlyLinks.length > 0 && (
        <div className="mt-6 border-t border-white/10 pt-3">
          <p className="px-4 text-xs uppercase tracking-[0.2em] text-white/50 mb-2">
            {locale === "fr" ? "Accès rapide" : "Quick links"}
          </p>
          {mobileOnlyLinks.map((link) => (
            <a key={link.id} href={link.href} className="block px-4 py-2 text-white/80 hover:bg-white/5 rounded-lg transition-colors">
              {link.title}
            </a>
          ))}
        </div>
      )}
    </div>
  );

  const renderSolutions = () => (
    <div className="flex flex-col flex-1">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button onClick={() => setView("main")} className="flex items-center gap-1">
          <BackIcon />
        </button>
        <h2 className="text-white font-semibold text-lg">{menu.solutions.title}</h2>
      </div>
      <div className="flex-1 px-4 py-3">
        <div className="flex flex-col gap-0">
          {solutionsGroups.map((group, index) => {
            const color = group.iconColor || SOLUTION_COLORS[index % SOLUTION_COLORS.length];
            const expanded = expandedSolutionsGroupId === group.id;
            return (
              <div key={group.id} className="flex items-start gap-2 py-6">
                <div className="flex h-6 px-0.5 items-start">
                  <div
                    className={cn("w-1 h-6 rounded-full", expanded ? undefined : "bg-white/5")}
                    style={expanded ? { backgroundColor: color } : undefined}
                  />
                </div>
                <div className="flex-1">
                  <button
                    onClick={() => setExpandedSolutionsGroupId(expanded ? null : group.id)}
                    className="flex items-start justify-between w-full text-left"
                  >
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg leading-6">{group.title.toUpperCase()}</h3>
                      {group.description && <p className="text-white/70 text-sm leading-[21px] mt-2">{group.description}</p>}
                    </div>
                    <div className="ml-2 mt-0.5 text-white">
                      {expanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    </div>
                  </button>

                  {expanded && (
                    <div className="mt-4 space-y-2">
                      {group.items.map((card) => {
                        const iconBg = hexToRgba(card.iconColor || color, 0.15, color);
                        return (
                          <a
                            key={card.id}
                            href={card.href}
                            className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5 transition-colors"
                          >
                            <div
                              className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0"
                              style={{ backgroundColor: iconBg }}
                            >
                              <span className="mainNav-icon text-lg" style={{ color: card.iconColor || color }}>
                                {card.icon ?? "•"}
                              </span>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-white font-bold text-lg leading-6">{card.title}</h4>
                              {card.description && (
                                <p className="text-white/70 text-sm leading-tight">{card.description}</p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  const renderCardsList = (section: PrimaryMenu) => (
    <div className="flex flex-col flex-1">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <button onClick={() => setView("main")} className="flex items-center gap-1">
          <BackIcon />
        </button>
        <h2 className="text-white font-semibold text-lg">{section.title}</h2>
      </div>
      <div className="flex-1 px-4 py-3 space-y-2">
        {section.cards.filter((card) => !card.mobileOnly).map((card) => {
          const color = card.iconColor || PRODUCTS_COLOR;
          const iconBg = hexToRgba(color, 0.15, color);
          return (
            <a key={card.id} href={card.href} className="flex items-start gap-4 p-4 rounded-lg hover:bg-white/5 transition-colors">
              <div className="w-8 h-8 rounded flex items-center justify-center" style={{ backgroundColor: iconBg }}>
                <span className="mainNav-icon text-lg" style={{ color }}>
                  {card.icon ?? "•"}
                </span>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-bold text-lg leading-6">{card.title}</h4>
                {card.description && <p className="text-white/70 text-sm leading-tight">{card.description}</p>}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-onplexus-darker overflow-hidden">
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between p-4 border-b border-white/10 flex-shrink-0">
          <Link to={locale === "fr" ? "/fr" : "/"}>
            <OnPlexusLogo />
          </Link>
          <button onClick={onClose} className="p-2">
            <CloseIcon />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {view === "main" && renderMain()}
          {view === "solutions" && renderSolutions()}
          {view === "products" && renderCardsList(menu.products)}
          {view === "services" && renderCardsList(menu.services)}
          {view === "partners" && renderCardsList(menu.partners)}
          {view === "company" && renderCardsList(menu.company)}
        </div>

        <div className="flex-shrink-0 p-4 bg-onplexus-darker/95 backdrop-blur">
          <Button
            asChild
            className="w-full bg-onplexus-gold text-onplexus-dark font-semibold text-base hover:bg-onplexus-gold-bright rounded-xl h-10"
          >
            <Link to={cta.href}>{cta.title}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
